import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { SectionLoader, EmptyState } from "@/components/common";
import { initials } from "@/lib/format";
import {
  Trophy, Flame, Sparkles, Star, EyeOff, ArrowRight, Crown, Medal,
  UserCheck, Briefcase, CalendarCheck, MessageCircleQuestion, UserPlus, Users, CheckCircle2,
} from "lucide-react";

const BADGE_ICONS = {
  UserCheck, Briefcase, CalendarCheck, MessageCircleQuestion, UserPlus, Users, Flame,
};

const LEVEL_STYLES = {
  Newcomer: "bg-stone-100 text-stone-700 border-stone-200",
  Regular: "bg-[var(--blue-soft,#DCE7F2)] text-[#082D50] border-[#9CC2E0]",
  Connector: "bg-[rgba(201,119,46,0.22)] text-[#8A4A18] border-[rgba(201,119,46,0.45)]",
  Ambassador: "bg-[#082D50] text-white border-[#082D50]",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

function RankIcon({ rank }) {
  if (rank === 1) return <Crown className="h-4 w-4 text-amber-500" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-slate-400" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-amber-700" />;
  return <span className="text-sm font-semibold tabular-nums text-muted-foreground">{rank}</span>;
}

export default function Rewards() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [levels, setLevels] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/gamify/me").then((r) => r.data).catch(() => null),
      api.get("/gamify/levels").then((r) => r.data).catch(() => null),
      api.get("/gamify/leaderboard").then((r) => r.data).catch(() => []),
    ]).then(([m, l, b]) => { setMe(m); setLevels(l); setBoard(b); });
  }, []);

  if (!me || !levels) return <SectionLoader label="Loading your rewards…" />;

  const lvl = me.level;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Rewards</h1>
        <p className="mt-1 text-muted-foreground">Earn points for showing up and getting involved. Level up, keep your streak, and climb the leaderboard.</p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="rewards-tab-overview"><Sparkles className="mr-1.5 h-4 w-4" />Overview</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="rewards-tab-leaderboard"><Trophy className="mr-1.5 h-4 w-4" />Leaderboard</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Hero */}
          <Card className="overflow-hidden border-border bg-card" data-testid="rewards-hero">
            <div className="fc-hero-bg p-6">
              <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge className={`border ${LEVEL_STYLES[lvl.name] || LEVEL_STYLES.Newcomer}`} data-testid="rewards-level-badge">
                      <Star className="mr-1 h-3.5 w-3.5" />{lvl.name}
                    </Badge>
                    {me.rank && (
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Trophy className="h-4 w-4 text-amber-500" /> Rank #{me.rank} of {me.total_ranked}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 font-serif text-4xl font-semibold tabular-nums text-foreground" data-testid="rewards-points">{me.points} <span className="text-lg font-normal text-muted-foreground">points</span></p>

                  {!lvl.is_max ? (
                    <div className="mt-4 max-w-md">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{lvl.name}</span>
                        <span className="tabular-nums">{lvl.points_to_next} pts to {lvl.next_name}</span>
                      </div>
                      <Progress value={lvl.progress_pct} className="mt-1.5 h-2.5" data-testid="rewards-progress" />
                    </div>
                  ) : (
                    <p className="mt-4 text-sm font-medium text-primary">You’ve reached the top level — Ambassador! 🎉</p>
                  )}
                </div>

                <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4" data-testid="rewards-streak">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(201,119,46,0.2)]">
                    <Flame className="h-6 w-6 text-[#C9772E]" />
                  </div>
                  <div>
                    <p className="font-serif text-2xl font-semibold tabular-nums text-foreground">{me.streak_weeks}</p>
                    <p className="text-xs text-muted-foreground">week streak</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
            {/* Badges */}
            <Card className="border-border bg-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-lg font-semibold text-foreground">Badges</h2>
                  <span className="text-sm text-muted-foreground tabular-nums">{me.earned_count}/{me.badges.length} earned</span>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {me.badges.map((b) => {
                    const Icon = BADGE_ICONS[b.icon] || Star;
                    return (
                      <div key={b.id} className={`flex items-start gap-3 rounded-xl border p-3 ${b.earned ? "border-primary/30 bg-secondary/40" : "border-dashed border-border opacity-60"}`} data-testid={`badge-${b.id}`}>
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${b.earned ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">{b.name}{b.earned && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}</p>
                          <p className="text-xs text-muted-foreground">{b.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {/* How to earn */}
              <Card className="border-border bg-card">
                <CardContent className="p-6">
                  <h2 className="font-serif text-lg font-semibold text-foreground">How to earn points</h2>
                  <div className="mt-3 divide-y divide-border">
                    {levels.scoring.map((s) => (
                      <div key={s.type} className="flex items-center justify-between py-2.5">
                        <span className="text-sm text-foreground">{s.label}</span>
                        <Badge variant="secondary" className="tabular-nums">+{s.points}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent activity */}
              <Card className="border-border bg-card">
                <CardContent className="p-6">
                  <h2 className="font-serif text-lg font-semibold text-foreground">Recent activity</h2>
                  {me.recent.length === 0 ? (
                    <p className="mt-3 text-sm text-muted-foreground">No activity yet — join an event to get started.</p>
                  ) : (
                    <div className="mt-3 space-y-2.5" data-testid="rewards-activity">
                      {me.recent.map((r, i) => (
                        <div key={`${r.created_at || "act"}-${i}`} className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm text-foreground">{r.label}</p>
                            <p className="text-xs text-muted-foreground">{timeAgo(r.created_at)}</p>
                          </div>
                          <span className="shrink-0 text-sm font-semibold tabular-nums text-primary">+{r.points}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* LEADERBOARD */}
        <TabsContent value="leaderboard" className="mt-6 space-y-4">
          {me.optout && (
            <Card className="border-amber-200 bg-amber-50/60"><CardContent className="flex items-center gap-3 p-4">
              <EyeOff className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-900">You’re hidden from the leaderboard. <Link to="/portal/account/profile" className="font-semibold underline" data-testid="rewards-optout-link">Change this in Privacy</Link> to compete.</p>
            </CardContent></Card>
          )}

          {!board || board.length === 0 ? (
            <EmptyState icon={Trophy} title="No rankings yet" description="As members earn points, they’ll show up here." testId="leaderboard-empty" />
          ) : (
            <Card className="border-border bg-card">
              <CardContent className="p-2 sm:p-3" data-testid="leaderboard-list">
                {board.map((row) => (
                  <div key={row.user_id} className={`flex items-center gap-3 rounded-xl px-3 py-3 ${row.is_me ? "bg-secondary" : "hover:bg-secondary/50"}`} data-testid={`leaderboard-row${row.is_me ? "-me" : ""}`}>
                    <div className="flex h-7 w-7 items-center justify-center"><RankIcon rank={row.rank} /></div>
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={row.avatar || undefined} alt={row.name} />
                      <AvatarFallback className="bg-primary text-sm text-primary-foreground">{initials(row.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{row.name}{row.is_me && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={`text-[11px] ${LEVEL_STYLES[row.level_name] || ""}`}>{row.level_name}</Badge>
                        {row.streak_weeks > 0 && <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Flame className="h-3 w-3 text-[#C9772E]" />{row.streak_weeks}</span>}
                      </div>
                    </div>
                    <span className="shrink-0 font-serif text-lg font-semibold tabular-nums text-foreground">{row.points}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
